/**
 * Credentials, inside the account they belong to.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A SECTION AND NOT A TAB
 *
 * A separate Credentials tab made the user hold two things in their head at
 * once: which key set exists, and which account points at it. Nobody thinks
 * "I will manage a credential" — they think "I will connect this account".
 *
 * BUT A LOGIN IS STILL NOT OWNED BY A CONNECTION
 *
 * This is the part the trading-journal layout does not have to deal with.
 * `[B1]`: XTS needs TWO credential pairs — Interactive for orders and Market
 * Data for prices — with separate logins, separate tokens and separate base
 * URLs. And a DEALER login (`isInvestorClient: false`) acts for MANY client
 * codes, so one key set legitimately backs several accounts.
 *
 * So this section lets you PICK an existing login or create a new one, and
 * says plainly when the one you picked is shared. Editing a shared login's
 * secrets changes them for every account using it, and that has to be
 * visible before the save, not discovered after it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react'
import { KeyRound, Plus, ShieldCheck } from 'lucide-react'
import type { BrokerLoginDto } from '@apna/shared'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const field =
  'mt-1 h-9 w-full rounded-md border border-border bg-background px-2.5 text-[13px] outline-none focus:border-primary'
const labelCls = 'text-[12px] font-medium text-foreground'

/** The sentinel for "use the platform's master feed", which is the default. */
/*
 * Was "use the platform's master feed". There is no platform feed any more
 * (ROADMAP 3.21): one streaming session exists per market-data key ([B14]),
 * so a shared key meant every trader evicting every other. The value is kept
 * as the "no credentials" option, which is now simply "no prices".
 */
const MASTER = '__master__'
const NONE = '__none__'
const NEW = '__new__'

export interface CredentialSectionProps {
  role: 'INTERACTIVE' | 'MARKET_DATA'
  providerKey: string
  logins: BrokerLoginDto[]
  value: string
  onChange: (loginId: string) => void
  /** How many connections already point at each login, for the shared warning. */
  usageByLogin: Record<string, number>
  onCreated: () => void
  /**
   * Deleting a credential set. Guarded by the caller, which refuses while
   * any account still points at it — a dealer pair removed out from under
   * the accounts it serves would silently disarm all of them.
   */
  onDelete: (id: string, label: string) => void
}

export function CredentialSection({
  role,
  providerKey,
  logins,
  value,
  onChange,
  usageByLogin,
  onCreated,
  onDelete,
}: CredentialSectionProps) {
  const [creating, setCreating] = useState(false)
  const [label, setLabel] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const orders = role === 'INTERACTIVE'
  const selected = logins.find((l) => l.id === value) ?? null
  const sharedWith = selected ? (usageByLogin[selected.id] ?? 0) - 1 : 0

  async function create() {
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ login: BrokerLoginDto }>('/broker/logins', {
        providerKey,
        role,
        label: label.trim(),
        apiKey: apiKey.trim(),
        apiSecret: apiSecret.trim(),
      })
      onChange(r.login.id)
      setCreating(false)
      setLabel('')
      setApiKey('')
      /*
       * The secret is cleared from component state the moment it is sent.
       * It is write-only everywhere else in this product — the server never
       * returns it — and keeping a copy in a React state tree that survives
       * the dialog closing would be the one place it lingers.
       */
      setApiSecret('')
      onCreated()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save those credentials.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center gap-2">
        <KeyRound size={14} className="text-muted-foreground" />
        <span className="text-[12.5px] font-semibold">
          {orders ? 'Orders credential' : 'Market data credential'}
        </span>
        <span className="flex-1" />
        {!creating && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px]"
            onClick={() => setCreating(true)}
            data-testid={`add-credential-${role}`}
          >
            <Plus size={12} /> New
          </Button>
        )}
      </div>

      <p className="mb-2 text-[11.5px] leading-[1.5] text-muted-foreground">
        {orders
          ? 'The Interactive pair places orders. Without one this account can read but not trade.'
          : 'Prices come from your own broker account. Add a market-data key here, then make one account your feed — a trader with none sees no live prices at all.'}
      </p>

      {!creating && (
        <Select
          value={value || (orders ? NONE : MASTER)}
          onValueChange={(v) => {
            if (v === NEW) {
              setCreating(true)
              return
            }
            onChange(v === MASTER || v === NONE ? '' : v)
          }}
        >
          <SelectTrigger className="h-9" data-testid={`credential-select-${role}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={orders ? NONE : MASTER}>
              {orders ? '— none (cannot trade) —' : '— none (no live prices) —'}
            </SelectItem>
            {logins.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.label}
                {l.isInvestorClient === false ? ' · dealer' : ''}
              </SelectItem>
            ))}
            <SelectItem value={NEW}>+ Add new credentials…</SelectItem>
          </SelectContent>
        </Select>
      )}

      {!creating && selected && (
        <button
          type="button"
          onClick={() => onDelete(selected.id, selected.label)}
          className="mt-2 text-[11px] text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
          data-testid={`delete-credential-${role}`}
        >
          Remove these credentials…
        </button>
      )}

      {!creating && sharedWith > 0 && (
        /*
         * A dealer pair acting for several client codes is the normal case,
         * not a mistake — but rotating its secret logs out every account on
         * it, so the fact has to be on screen before anyone edits it.
         */
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-warning" data-testid={`shared-${role}`}>
          <ShieldCheck size={12} className="mt-[1px] shrink-0" />
          Shared with {sharedWith} other account{sharedWith === 1 ? '' : 's'}. Changing these keys
          affects all of them.
        </p>
      )}

      {creating && (
        <div className="space-y-2.5">
          <label className="block">
            <span className={labelCls}>Name these credentials</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={orders ? 'Desk orders key' : 'Desk feed key'}
              className={field}
              data-testid={`credential-label-${role}`}
            />
          </label>
          <div className="grid grid-cols-2 gap-2.5">
            <label className="block">
              <span className={labelCls}>API key</span>
              <input
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className={cn(field, 'num')}
                autoComplete="off"
                data-testid={`credential-key-${role}`}
              />
            </label>
            <label className="block">
              <span className={labelCls}>API secret</span>
              <input
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                className={cn(field, 'num')}
                autoComplete="off"
                data-testid={`credential-secret-${role}`}
              />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Encrypted before it is stored, and never returned by the server — not to this screen
            and not to any other.
          </p>
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="h-7 text-[11px]"
              disabled={busy || !label.trim() || !apiKey.trim() || !apiSecret.trim()}
              onClick={() => void create()}
              data-testid={`credential-save-${role}`}
            >
              {busy ? 'Saving…' : 'Save credentials'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[11px]"
              onClick={() => {
                setCreating(false)
                setApiSecret('')
                setError(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
